import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { REACT_APP_API_BASE } from '../../../config';
import { buildTableColumns } from '../classifications/classificationTableConfig';
import CreateClassificationForm from '../classifications/CreateClassificationForm';
import Takeover, { AltTakeover } from '../../takeover';
import { Button } from '../../forms';
import SpinnerDots from '../../spinner/dots';
import ClassificationsTable from '../classifications/ClassificationsTable';
import ClassificationEmptyState from '../classifications/ClassificationEmptyState';
import './panel-classifications.scss';

const InlineEditClassificationButton = ({
    classificationId,
    scenarioId,
    onSaved,
    formProps
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    };

    return (
        <>
            <button type="button" className="panel-classifications__action-btn panel-classifications__action-btn--edit" onClick={toggleOpen} />
            <AltTakeover isOpen={isOpen} toggleTakeover={toggleOpen}>
                {isOpen && (
                    <CreateClassificationForm
                        {...formProps}
                        isEdit={true}
                        classificationId={classificationId}
                        scenarioId={scenarioId}
                        saveSuccessCallback={() => {
                            toggleOpen();
                            if (onSaved) {
                                onSaved();
                            }
                        }}
                        cancelButton={
                            <button onClick={toggleOpen} className="classification-form__cancel">
                                Avbryt
                            </button>
                        }
                    />
                )}
            </AltTakeover>
        </>
    );
};

/**
 * PanelClassifications - Tab-komponent för att hantera klassificeringar
 * Innehåller tabellen och formuläret för att skapa/redigera klassificeringar
 */
const PanelClassifications = ({
    allDistricts = [],
    match,
    classifications = [],
    isLoading = false,
    reload,
    fetch,
    ...props
}) => {
    const [isCreateClassificationOpen, setIsCreateClassificationOpen] = useState(false);
    const [deleteDialogState, setDeleteDialogState] = useState({ isOpen: false, classification: null, isDeleting: false });
    const { scenarioId: routeScenarioId } = useParams();
    const scenarioId = routeScenarioId || null;

    function openDeleteDialog(classification) {
        setDeleteDialogState({ isOpen: true, classification, isDeleting: false });
    }

    function closeDeleteDialog() {
        setDeleteDialogState({ isOpen: false, classification: null, isDeleting: false });
    }

    async function handleDeleteClassification(classificationId) {
        setDeleteDialogState(prev => ({ ...prev, isDeleting: true }));
        try {
            const rawResponse = await fetch(`${REACT_APP_API_BASE}/salesdistrict/classification/delete/${classificationId}`, {
                method: 'DELETE'
            });

            if (!rawResponse.ok) {
                throw new Error('Failed to delete classification');
            }

            closeDeleteDialog();
            reload();
        } catch (error) {
            console.error(`Failed to delete classification ${classificationId}:`, error);
            setDeleteDialogState(prev => ({ ...prev, isDeleting: false }));
        }
    }

    const columns = useMemo(() => {
        const baseColumns = buildTableColumns({
            renderActions: (row) => (
                <div className="panel-classifications__actions">
                    <InlineEditClassificationButton
                        classificationId={row.classificationId}
                        scenarioId={row.salesDistrictScenarioId}
                        formProps={props}
                        onSaved={reload}
                    />
                    <button
                        type="button"
                        className="panel-classifications__action-btn panel-classifications__action-btn--delete"
                        onClick={() => openDeleteDialog(row)}
                    />
                </div>
            )
        });

        return baseColumns
            .filter((column) => column.key !== 'salesSpace')
            .map((column) => {
                if (column.key !== 'frequency') {
                    return column;
                }

                return {
                    ...column,
                    label: 'Besök per säljcykel',
                    render: (row) => {
                        const selectedFrequency = row.classificationFrequencies?.find((freq) => freq.selected)
                            ?? row.classificationFrequencies?.[0];

                        if (!selectedFrequency) {
                            return null;
                        }

                        return selectedFrequency.classificationFrequencyValue;
                    }
                };
            });
    }, [reload, props]);

    const openCreateClassificationForm = () => setIsCreateClassificationOpen(true);
    const closeCreateClassificationForm = () => setIsCreateClassificationOpen(false);

    return (
        <div className="panel-classifications">
            <header className="panel-classifications__header">
                <h2 className="panel-classifications__title">Klassificeringar</h2>
                {classifications.length > 0 && (
                    <button
                        className="panel-classifications__add-button"
                        type="button"
                        onClick={openCreateClassificationForm}
                    >
                        + Ny klassificering
                    </button>
                )}
            </header>

            <ClassificationsTable
                rows={classifications}
                columns={columns}
                className="table table--num"
                scrollable
                emptyState={
                    <ClassificationEmptyState
                        linkComponent={
                            <button
                                className="panel-classifications__add-button"
                                type="button"
                                onClick={openCreateClassificationForm}
                            >
                                + Ny klassificering
                            </button>
                        }
                    />
                }/>

            <AltTakeover
                heading="Skapa ny klassificering"
                isOpen={isCreateClassificationOpen}
                toggleTakeover={setIsCreateClassificationOpen}
            >
                {isCreateClassificationOpen && (
                    <CreateClassificationForm
                        {...props}
                        isEdit={false}
                        scenarioId={scenarioId}
                        saveSuccessCallback={() => {
                            closeCreateClassificationForm();
                            reload();
                        }}
                        cancelButton={
                            <button
                                onClick={closeCreateClassificationForm}
                                className="classification-form__cancel"
                            >
                                Avbryt
                            </button>
                        }
                    />
                )}
            </AltTakeover>

            <Takeover
                isOpen={deleteDialogState.isOpen}
                toggleTakeover={closeDeleteDialog}
                heading="TA BORT KLASSIFICERING"
            >
                <div style={{ padding: '20px' }}>
                    <p>Vill du ta bort klassificeringen permanent?</p>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <Button
                            type="button"
                            modifiers={{ 'districts__takeover-controls-btn--danger': true }}
                            onClick={() => handleDeleteClassification(deleteDialogState.classification?.classificationId)}
                            disabled={deleteDialogState.isDeleting}
                        >
                            {deleteDialogState.isDeleting ? 'Raderar...' : 'Ta bort klassificering'}
                        </Button>
                        <Button
                            type="button"
                            modifiers={{ 'districts__takeover-controls-btn--outline': true }}
                            onClick={closeDeleteDialog}
                            disabled={deleteDialogState.isDeleting}
                        >
                            Avbryt
                        </Button>
                    </div>
                    {deleteDialogState.isDeleting && <SpinnerDots />}
                </div>
            </Takeover>
        </div>
    );
};

export default PanelClassifications;
